import React, { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  UserCircleIcon,
  BriefcaseIcon,
  DocumentTextIcon,
  CheckBadgeIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  ClipboardDocumentCheckIcon,
  InformationCircleIcon,
  PaperClipIcon,
  ArrowDownTrayIcon, // Icon for download link
} from '@heroicons/react/24/outline';

// --- CONFIGURATION & MOCK DATA ---

// Define the steps for the multi-step form
const ONBOARDING_STEPS = [
  { id: 1, name: 'Personal Information', icon: UserCircleIcon, fields: ['fullName', 'email', 'phone', 'address', 'dob', 'emergencyContact'] },
  { id: 2, name: 'Employment Details', icon: BriefcaseIcon, fields: ['jobTitle', 'department', 'managerId', 'salary', 'joiningDate'] },
  { id: 3, name: 'Document Submission', icon: DocumentTextIcon, fields: ['idProofFile', 'bankDetailsFile', 'educationFile'] },
  { id: 4, name: 'Policy Acknowledgment', icon: CheckBadgeIcon, fields: ['ndaSigned', 'handbookSigned', 'codeOfConductSigned'] },
];

const initialFormData = {
  // Step 1: Personal
  fullName: '', email: '', phone: '', address: '', dob: '', emergencyContact: '',
  // Step 2: Employment
  jobTitle: '', department: '', managerId: 'manager_101', salary: '', joiningDate: '',
  // Step 3: Documents (using placeholder file names)
  idProofFile: null, bankDetailsFile: null, educationFile: null,
  // Step 4: Policies
  ndaSigned: false, handbookSigned: false, codeOfConductSigned: false,
};

// --- COMPONENTS ---

// Generic Input Field
const FormInput = ({ id, label, type = 'text', value, onChange, placeholder, required = true }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-semibold text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type={type}
      id={id}
      name={id}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm transition font-roboto"
    />
  </div>
);

// Generic File Upload Component
const FileUpload = ({ id, label, onFileChange, fileName, required = true }) => (
  <div className="border border-dashed border-gray-300 rounded-lg p-5 bg-white hover:bg-indigo-50 transition-colors cursor-pointer">
    <label htmlFor={id} className="cursor-pointer">
      <div className="flex items-center space-x-3">
        <PaperClipIcon className="h-6 w-6 text-indigo-500 flex-shrink-0" />
        <p className="text-sm font-semibold text-gray-700">
          {label} {required && <span className="text-red-500">*</span>}
        </p>
      </div>
      <p className="text-xs text-gray-500 mt-2 ml-9">
        {fileName ? (
          <span className="text-green-600 font-medium">{fileName} uploaded. Click to change.</span>
        ) : (
          'Upload PDF, JPG, or PNG (Max 5MB).'
        )}
      </p>
    </label>
    <input
      type="file"
      id={id}
      name={id}
      onChange={onFileChange}
      className="hidden"
      required={required && !fileName}
      accept=".pdf,.jpg,.jpeg,.png"
    />
  </div>
);

// Generic Checkbox for Policies with Download Link
const PolicyCheckbox = ({ id, label, checked, onChange, policyName }) => {
    
    // Handler for the mock policy download
    const handlePolicyView = (e) => {
        e.preventDefault(); // Prevent form submission if clicked inside a form
        // In a real application, this would trigger a download or open a viewer.
        // We'll simulate a file download confirmation here.
        console.log(`Simulating download/view for: ${policyName} Policy PDF.`);
        alert(`You are viewing the full text for the ${policyName} Policy. Please review carefully before acknowledging.`);
    };

    return (
      <div className="border border-gray-200 p-4 rounded-lg bg-white shadow-sm">
        <div className="relative flex items-start justify-between">
          <div className="flex h-6 items-center">
            <input
              id={id}
              name={id}
              type="checkbox"
              checked={checked}
              onChange={onChange}
              required
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600 flex-shrink-0 mt-1"
            />
          </div>
          <div className="ml-3 text-sm leading-6 flex-grow">
            <label htmlFor={id} className="font-semibold text-gray-700 cursor-pointer">
              {label} <span className="text-red-500">*</span>
            </label>
            <p className="text-gray-500 text-xs">I have read, understood, and agree to the terms of this document.</p>
          </div>
          <motion.button
              type="button"
              onClick={handlePolicyView}
              className="ml-4 flex-shrink-0 inline-flex items-center text-xs font-semibold text-indigo-600 hover:text-indigo-800 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
          >
              <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
              View Policy (PDF)
          </motion.button>
        </div>
      </div>
    );
};


// --- STEP COMPONENTS ---

const Step1PersonalInfo = ({ data, handleChange }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
    <FormInput id="fullName" label="Legal Full Name" value={data.fullName} onChange={handleChange} placeholder="As per official documents" />
    <FormInput id="email" label="Personal Email Address" type="email" value={data.email} onChange={handleChange} placeholder="personal@example.com" />
    <FormInput id="phone" label="Contact Phone Number" value={data.phone} onChange={handleChange} placeholder="+1 (555) 000-0000" />
    <FormInput id="dob" label="Date of Birth" type="date" value={data.dob} onChange={handleChange} />
    <div className="md:col-span-2">
      <FormInput id="address" label="Permanent Residential Address" value={data.address} onChange={handleChange} placeholder="Street, City, State, Zip/Postal Code" />
    </div>
    <FormInput id="emergencyContact" label="Emergency Contact Name/Number" value={data.emergencyContact} onChange={handleChange} placeholder="Name and Phone Number" />
  </div>
);

const Step2EmploymentDetails = ({ data, handleChange }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
    <FormInput id="jobTitle" label="Designation (Job Title)" value={data.jobTitle} onChange={handleChange} placeholder="e.g., Software Engineer I" />
    <FormInput id="department" label="Department" value={data.department} onChange={handleChange} placeholder="e.g., Technology/Finance" />
    <FormInput id="joiningDate" label="Official Joining Date" type="date" value={data.joiningDate} onChange={handleChange} />
    <FormInput id="salary" label="Annual Salary (Gross)" type="number" value={data.salary} onChange={handleChange} placeholder="e.g., 85000" />
    <div className="md:col-span-2">
        <label htmlFor="managerId" className="block text-sm font-semibold text-gray-700 mb-1">
            Reporting Manager
        </label>
        <select
            id="managerId"
            name="managerId"
            value={data.managerId}
            onChange={handleChange}
            className="w-full pl-4 pr-10 py-2.5 border border-gray-300 bg-white rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm transition font-roboto"
        >
            <option value="manager_101">Jane Doe (ID: 101) - Head of Engineering</option>
            <option value="manager_102">John Smith (ID: 102) - HR Director</option>
        </select>
        <p className="mt-1 text-xs text-gray-500 flex items-center">
            <InformationCircleIcon className="h-4 w-4 mr-1 text-indigo-400" />
            Your initial reporting manager for this role.
        </p>
    </div>
  </div>
);

const Step3DocumentSubmission = ({ data, handleFileChange }) => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
    <FileUpload
      id="idProofFile"
      label="Photo ID Proof (Passport/License)"
      onFileChange={handleFileChange}
      fileName={data.idProofFile?.name}
    />
    <FileUpload
      id="bankDetailsFile"
      // Updated label as requested
      label="Bank Account Proof Document"
      onFileChange={handleFileChange}
      fileName={data.bankDetailsFile?.name}
    />
    <FileUpload
      id="educationFile"
      label="Highest Education Certificate"
      onFileChange={handleFileChange}
      fileName={data.educationFile?.name}
    />
    <div className="md:col-span-3 p-4 bg-yellow-50 rounded-lg text-sm text-yellow-700 flex items-center border border-yellow-300">
        <InformationCircleIcon className="h-5 w-5 mr-3 flex-shrink-0" />
        All documents must be clear, legible, and properly named for verification.
    </div>
  </div>
);

const Step4PolicyAcknowledgement = ({ data, handleChange }) => (
  <div className="space-y-4">
    <PolicyCheckbox
      id="ndaSigned"
      label="Non-Disclosure Agreement (NDA)"
      policyName="NDA"
      checked={data.ndaSigned}
      onChange={handleChange}
    />
    <PolicyCheckbox
      id="handbookSigned"
      label="Employee Handbook & Conduct Policy"
      policyName="Employee Handbook"
      checked={data.handbookSigned}
      onChange={handleChange}
    />
    <PolicyCheckbox
      id="codeOfConductSigned"
      label="IT Usage and Data Security Policy"
      policyName="IT Security"
      checked={data.codeOfConductSigned}
      onChange={handleChange}
    />
    <div className="p-4 bg-indigo-50 rounded-lg text-sm text-indigo-700 flex items-start border border-indigo-300">
        <InformationCircleIcon className="h-5 w-5 mt-0.5 mr-3 flex-shrink-0" />
        You must view and acknowledge all three policies before completing your onboarding.
    </div>
  </div>
);

// --- MAIN COMPONENT ---

const EmployeeOnboarding = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [completionMessage, setCompletionMessage] = useState(null);

  const totalSteps = ONBOARDING_STEPS.length;
  const CurrentStepIcon = ONBOARDING_STEPS.find(s => s.id === currentStep)?.icon;

  const getStepComponent = useCallback(() => {
    switch (currentStep) {
      case 1: return <Step1PersonalInfo data={formData} handleChange={handleChange} />;
      case 2: return <Step2EmploymentDetails data={formData} handleChange={handleChange} />;
      case 3: return <Step3DocumentSubmission data={formData} handleFileChange={handleFileChange} />;
      case 4: return <Step4PolicyAcknowledgement data={formData} handleChange={handleChange} />;
      default: return null;
    }
  }, [currentStep, formData]);

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // For demonstration, storing file name and a mock file object
      setFormData(prev => ({ 
        ...prev, 
        [e.target.name]: { name: file.name, size: file.size, type: file.type } 
      }));
    }
  };

  const isStepValid = useMemo(() => {
    const stepFields = ONBOARDING_STEPS.find(s => s.id === currentStep)?.fields || [];
    
    // Check if all required fields in the current step are filled
    for (const field of stepFields) {
      const value = formData[field];
      // Check for null, empty string, false (for checkboxes), or object without a name (for files)
      if (value === null || value === '' || value === false || (typeof value === 'object' && !value.name)) {
        return false;
      }
    }
    return true;
  }, [currentStep, formData]);


  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isStepValid) return;

    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
    } else {
      // Final Submission Logic
      setIsSubmitting(true);
      
      // Simulate API call delay
      setTimeout(() => {
        setIsSubmitting(false);
        setCompletionMessage(
          `Thank you, ${formData.fullName}! Your onboarding is complete. We have successfully submitted all your data and documents for final verification. You will receive a Welcome Email on your first day, ${formData.joiningDate}.`
        );
        // Reset form data for a clean state if needed later
        // setFormData(initialFormData); 
        setCurrentStep(totalSteps + 1); // Move to a final success state
      }, 1500);
    }
  };

  const ProgressTracker = () => (
    <div className="flex justify-between items-center py-4 px-8 bg-white border-b border-gray-100 rounded-t-2xl shadow-sm">
      {ONBOARDING_STEPS.map((step) => (
        <div key={step.id} className="relative flex items-center group">
          <div className="flex flex-col items-center">
            <div
              className={`w-8 h-8 flex items-center justify-center rounded-full border-2 transition-all duration-300 
                ${currentStep > step.id ? 'bg-indigo-600 border-indigo-600' : 
                  currentStep === step.id ? 'bg-indigo-50 border-indigo-600' : 
                  'bg-white border-gray-300'
                }`}
            >
              {currentStep > step.id ? (
                <CheckBadgeIcon className="h-4 w-4 text-white" />
              ) : (
                <step.icon className={`h-4 w-4 ${currentStep === step.id ? 'text-indigo-600' : 'text-gray-400'}`} />
              )}
            </div>
            {/* The line connecting the steps */}
            {step.id < totalSteps && (
              <div className={`absolute top-4 h-0.5 w-16 xl:w-24 transition-all duration-500 -mt-0.5 ${currentStep > step.id ? 'bg-indigo-600' : 'bg-gray-200'} z-0`} style={{ left: '50%', transform: 'translateX(-50%)' }}/>
            )}
            <p className={`mt-2 text-xs font-medium text-center transition-colors duration-300 ${currentStep === step.id ? 'text-indigo-600 font-bold' : 'text-gray-500'}`}>
              {step.name}
            </p>
          </div>
        </div>
      ))}
    </div>
  );

  const SuccessScreen = () => (
    <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="p-10 text-center flex flex-col items-center justify-center min-h-[500px]"
    >
        <ClipboardDocumentCheckIcon className="h-20 w-20 text-green-500 mb-6 animate-pulse" />
        <h2 className="text-3xl font-extrabold font-poppins text-gray-900 mb-4">
            Onboarding Complete!
        </h2>
        <p className="text-lg text-gray-600 max-w-xl font-roboto">
            {completionMessage}
        </p>
        <div className="mt-8">
            <button
                onClick={() => {
                    // Navigate to the main dashboard or profile page
                    alert("Navigating to Employee Dashboard (Simulated)");
                }}
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-semibold rounded-lg shadow-md text-white bg-indigo-600 hover:bg-indigo-700 transition-colors"
            >
                View Dashboard
            </button>
        </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gray-50 font-roboto">
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700;800&family=Roboto:wght@400;500;700&display=swap" />

      <div className="max-w-6xl mx-auto py-10 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-6 bg-white rounded-2xl shadow-xl border border-indigo-100"
        >
          <h1 className="text-3xl font-extrabold text-gray-900 font-poppins">
            New Employee Onboarding Wizard
          </h1>
          <p className="mt-1 text-gray-500 text-sm font-roboto">
            Welcome! Please complete the four steps below to finalize your employment process.
          </p>
        </motion.div>

        {/* Multi-Step Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
          
          {currentStep <= totalSteps && <ProgressTracker />}

          {/* Form Content */}
          {currentStep <= totalSteps ? (
            <motion.form 
                key={currentStep}
                onSubmit={handleSubmit} 
                className="p-8"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
            >
              <div className="mb-8">
                <div className="flex items-center text-xl font-bold font-poppins text-gray-800">
                    {CurrentStepIcon && <CurrentStepIcon className="h-6 w-6 mr-2 text-indigo-500" />}
                    {ONBOARDING_STEPS.find(s => s.id === currentStep)?.name}
                </div>
                <p className="text-sm text-gray-500 mt-1 font-roboto">Step {currentStep} of {totalSteps}: Please provide the required information below.</p>
              </div>

              <div className="min-h-[300px] mb-8">
                {getStepComponent()}
              </div>

              {/* Navigation Footer */}
              <div className="flex justify-between border-t pt-6">
                <motion.button
                  type="button"
                  onClick={() => setCurrentStep(prev => prev - 1)}
                  disabled={currentStep === 1 || isSubmitting}
                  className={`inline-flex items-center px-6 py-2.5 border border-gray-300 text-base font-semibold rounded-lg text-gray-700 bg-white transition-colors font-roboto ${
                    currentStep === 1 || isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <ChevronLeftIcon className="h-5 w-5 mr-2" />
                  Previous
                </motion.button>

                <motion.button
                  type="submit"
                  disabled={!isStepValid || isSubmitting}
                  className={`inline-flex items-center px-6 py-2.5 text-base font-semibold rounded-lg shadow-md transition-all font-roboto ${
                    !isStepValid || isSubmitting
                      ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
                      : 'text-white bg-indigo-600 hover:bg-indigo-700 focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {isSubmitting ? (
                    'Submitting...'
                  ) : currentStep === totalSteps ? (
                    'Finalize & Complete Onboarding'
                  ) : (
                    <>
                      Next Step <ChevronRightIcon className="h-5 w-5 ml-2" />
                    </>
                  )}
                </motion.button>
              </div>
            </motion.form>
          ) : (
            <SuccessScreen />
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeOnboarding;